import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import TextareaAutosize from 'react-textarea-autosize';
import { useEffect, useState } from 'react';
import Customer from '../../../models/Customer';
import commonStyles from '../common.module.css';
import Input from '../../common/Input/Input';
import Select from '../../common/Select/Select';
import { allowedStatuses } from '../../../utils/util';
import styles from './BasicInformation.module.css';
import Error from '../../common/Error/Error';
import warningLogo from '../../../assets/images/warning.png';
import { isValidEmail, isValidPhone } from '../../../utils/validation';
import CountrySelect from '../../common/CountrySelect/CountrySelect';
import Button from '../../common/Button/Button';
import Portfolio from '../../../models/Portfolio';

type LegalBasicInformationEditProps = {
  customer: Customer;
  onUpdate: (updatedCustomer: Customer) => void;
  error: string;
};

function LegalBasicInformationEdit(props: LegalBasicInformationEditProps) {
  const { t } = useTranslation();
  const [showError, setShowError] = useState(false);
  const [duplicatePortfolio, setDuplicatePortfolio] = useState(999999);

  const addNewEverisePortfolioId = () => {
    props.onUpdate({
      ...props.customer,
      portfolios: [
        ...(props.customer.portfolios as Portfolio[]),
        {
          everisePortfolioId: undefined,
          personId: undefined,
        },
      ],
    });
  };

  useEffect(() => {
    if (!props.customer.portfolios?.length) {
      addNewEverisePortfolioId();
    }
  }, []);

  useEffect(() => {
    setShowError(props.error !== '');
  }, [props.error]);

  const handleCommentUpdate = (event: any) => {
    event.preventDefault();
    props.onUpdate({
      ...props.customer,
      comments: event.target.value,
    });
  };

  const deleteEverisePortfolioId = (id: number) => {
    const portfoliosCopy = JSON.parse(JSON.stringify(props.customer.portfolios));
    props.onUpdate({
      ...props.customer,
      portfolios: portfoliosCopy.toSpliced(id, 1),
    });
  };

  const updateEverisePortfolioId = (value: any, index: number) => {
      const duplicate = props.customer.portfolios?.
        find(x => x.everisePortfolioId?.toString() === value);
      if (duplicate !== undefined) {
          setDuplicatePortfolio(index);
          setShowError(true);
      } else {
          setDuplicatePortfolio(999999);
          setShowError(false);
      }
    const updatedPortfolios = props.customer.portfolios?.map((portfolio, i) => {
      if (i === index) {
        return {
          ...portfolio,
          personId: props.customer.id,
          everisePortfolioId: Number(value),
        };
      }
      return portfolio;
    });
    props.onUpdate({
      ...props.customer,
      portfolios: updatedPortfolios,
    });
  };

  return (
    <div className={commonStyles.container}>
      <div className={commonStyles.header}>
        {showError && (
          <Error
            messageText={props.error}
            logoSrc={warningLogo}
            logoAltText={t('customer.validation.warningLogo')}
          />
        )}
      </div>
      <div className={commonStyles.row}>
        <div className={styles.field}>
          <div className={styles.label}>{t('customer.dashboard.basicInformation.label.cif')}</div>
          <div>{props.customer.cif}</div>
        </div>
        <div className={classNames(styles.field, styles.longField)}>
          <div className={styles.label}>
            {t('customer.dashboard.basicInformation.label.activity.title')}
          </div>
          <div
            className={classNames(
              styles.activityContainer,
              props.customer.active ? styles.active : styles.inactive,
            )}
          />
          <div>
            {props.customer.active
              ? t('customer.dashboard.basicInformation.label.activity.active')
              : t('customer.dashboard.basicInformation.label.activity.inactive')}
          </div>
        </div>
      </div>
      <div className={commonStyles.row}>
        <div className={styles.field}>
          <Input
            id="name"
            placeholder={t('customer.basicInfo.placeholder.companyName')}
            type="text"
            maxLength={70}
            label={t('customer.basicInfo.label.companyName')}
            required
            value={props.customer.name}
            onUpdate={(value) =>
              props.onUpdate({
                ...props.customer,
                name: value,
              })
            }
            error={showError && props.customer.name === ''}
          />
        </div>
        <div className={styles.field}>
          <Input
            id="registrationCode"
            placeholder={t('customer.basicInfo.placeholder.registrationCode')}
            type="text"
            maxLength={35}
            label={t('customer.basicInfo.label.registryCode')}
            required
            value={props.customer.registrationCode}
            onUpdate={(value) =>
              props.onUpdate({
                ...props.customer,
                registrationCode: value.toLowerCase(),
              })
            }
            error={showError && props.customer.registrationCode === ''}
          />
        </div>
        <div className={styles.field}>
          <CountrySelect
            required
            label={t('customer.basicInfo.label.country')}
            isLongLabel
            value={props.customer.country}
            onUpdate={(value) =>
              props.onUpdate({
                ...props.customer,
                country: value,
              })
            }
            error={showError && props.customer.country === ''}
          />
        </div>
      </div>
      <div className={commonStyles.row}>
        <div className={styles.field}>
          <Input
            id="phone"
            placeholder={t('customer.basicInfo.placeholder.phone')}
            type="text"
            maxLength={15}
            label={t('customer.basicInfo.label.phone')}
            value={props.customer.phone}
            onUpdate={(value) =>
              props.onUpdate({
                ...props.customer,
                phone: value,
              })
            }
            error={showError && !isValidPhone(props.customer.phone, true)}
          />
        </div>
        <div className={styles.field}>
          <Input
            id="email"
            placeholder={t('customer.basicInfo.placeholder.email')}
            type="email"
            label={t('customer.basicInfo.label.email')}
            required
            value={props.customer.email}
            onUpdate={(value) =>
              props.onUpdate({
                ...props.customer,
                email: value,
              })
            }
            error={showError && !isValidEmail(props.customer.email, false)}
          />
        </div>
        <div className={styles.field}>
          <div>
            <Select
              id="language"
              options={[{ value: 'EN' }, { value: 'EE' }, { value: 'LV' }, { value: 'LT' }]}
              label={t('customer.basicInfo.label.language')}
              required
              value={props.customer.preferredLanguage}
              onUpdate={(value) =>
                props.onUpdate({
                  ...props.customer,
                  preferredLanguage: value,
                })
              }
              error={showError && props.customer.preferredLanguage === ''}
            />
          </div>
        </div>
      </div>
      <div className={commonStyles.row}>
        <div className={styles.field}>
          <Select
            id="status"
            options={allowedStatuses(props.customer.customerStatus!)}
            label={t('customer.basicInfo.label.status')}
            required
            value={props.customer.customerStatus}
            onUpdate={(value) =>
              props.onUpdate({
                ...props.customer,
                customerStatus: value,
              })
            }
          />
        </div>
        <div className={classNames(styles.field, styles.longField, styles.portfolios)}>
          <div>
            {props.customer.portfolios
              ?.sort((a, b) => a.id! - b.id!)
              .map((portfolio, index) => (
                <div key={portfolio.id} className={styles.everisePortfolioId}>
                  <Input
                    key={portfolio.id}
                    id="everiseId"
                    placeholder={t('customer.basicInfo.placeholder.everisePortfolioId')}
                    type="number"
                    label={`${t('customer.basicInfo.label.everisePortfolioId')} ${
                      index > 0 ? `#${index + 1}` : ''
                    }`}
                    value={portfolio.everisePortfolioId || ''}
                    onUpdate={(value) => updateEverisePortfolioId(value, index)}
                    error={showError && duplicatePortfolio === index}
                  />
                  {props.customer.portfolios?.length && props.customer.portfolios?.length > 1 && (
                    <button
                      type="button"
                      className={styles.cancelButton}
                      onClick={() => deleteEverisePortfolioId(index)}
                    >
                      X
                    </button>
                  )}
                </div>
              ))}
          </div>
          <div className={styles.addButton}>
            <Button handleClick={addNewEverisePortfolioId} text={t('common.add')} />
          </div>
        </div>
      </div>
      <div className={styles.comments}>
        <div className={styles.label}>
          {t('customer.dashboard.basicInformation.label.comments')}
        </div>
        <TextareaAutosize
          className={styles.textarea}
          value={props.customer.comments || ''}
          minRows={5}
          onChange={handleCommentUpdate}
        />
      </div>
    </div>
  );
}

export default LegalBasicInformationEdit;
